import React from 'react';
import { ChevronDown, Loader2, Mic, Square } from 'lucide-react';

// Recognition support varies by browser/service; this is a language picker, not
// a guarantee that every browser supports every language listed here.
const LANGUAGES = [
  ['ar-SA', 'العربية'], ['bn-BD', 'বাংলা'], ['zh-CN', '中文（普通话）'],
  ['zh-TW', '中文（台灣）'], ['cs-CZ', 'Čeština'], ['da-DK', 'Dansk'],
  ['nl-NL', 'Nederlands'], ['en-US', 'English (US)'], ['en-GB', 'English (UK)'],
  ['fi-FI', 'Suomi'], ['fr-FR', 'Français'], ['de-DE', 'Deutsch'],
  ['el-GR', 'Ελληνικά'], ['he-IL', 'עברית'], ['hi-IN', 'हिन्दी'],
  ['id-ID', 'Bahasa Indonesia'], ['it-IT', 'Italiano'], ['ja-JP', '日本語'],
  ['ko-KR', '한국어'], ['ms-MY', 'Bahasa Melayu'], ['no-NO', 'Norsk'],
  ['pl-PL', 'Polski'], ['pt-BR', 'Português (Brasil)'], ['pt-PT', 'Português (Portugal)'],
  ['ro-RO', 'Română'], ['ru-RU', 'Русский'], ['es-ES', 'Español (España)'],
  ['es-MX', 'Español (México)'], ['sv-SE', 'Svenska'], ['ta-IN', 'தமிழ்'],
  ['te-IN', 'తెలుగు'], ['th-TH', 'ไทย'], ['tr-TR', 'Türkçe'],
  ['uk-UA', 'Українська'], ['ur-PK', 'اردو'], ['vi-VN', 'Tiếng Việt'],
];

export function VoiceInputButtons({ voice, disabled, settingsOpen, onToggleSettings }) {
  const label = !voice.supported ? 'Voice input is unavailable in this browser. You can use your device keyboard dictation.'
    : voice.active ? 'Stop voice input' : 'Start voice input';
  return (
    <div className="flex shrink-0 items-center">
      <button
        type="button"
        aria-label={label}
        aria-pressed={voice.active}
        title={label}
        disabled={!voice.supported || disabled || voice.phase === 'stopping'}
        onClick={voice.active ? voice.stop : voice.start}
        className={`flex items-center justify-center rounded-xl p-2.5 transition-colors disabled:opacity-40 ${voice.active ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}
      >
        {voice.phase === 'starting' || voice.phase === 'stopping' ? <Loader2 size={16} className="animate-spin" />
          : voice.active ? <Square size={16} fill="currentColor" /> : <Mic size={16} />}
      </button>
      {voice.supported && <button type="button" aria-label="Voice input settings" aria-expanded={settingsOpen} onClick={onToggleSettings} className="rounded-lg px-1 py-2.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
        <ChevronDown size={12} />
      </button>}
    </div>
  );
}

export function VoiceInputDetails({ voice, settingsOpen }) {
  return (
    <>
      {voice.supported && settingsOpen && <div className="mt-2 space-y-1 px-1 text-xs text-gray-500">
        <label className="flex flex-wrap items-center gap-2">
          Voice language
          <select aria-label="Voice language" value={voice.language} disabled={voice.active} onChange={(event) => voice.setLanguage(event.target.value)} className="min-w-0 max-w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-gray-700">
            {!LANGUAGES.some(([code]) => code === voice.language) && <option value={voice.language}>{voice.language} (browser language)</option>}
            {LANGUAGES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
        </label>
        <p>Your browser handles transcription and may send audio to its speech service. Language support varies by browser.</p>
      </div>}
      {voice.active && <div className="mt-2 px-1 text-xs text-gray-500">
        <p role="status">{voice.phase === 'starting' ? 'Waiting for microphone…' : voice.phase === 'stopping' ? 'Finishing transcription…' : 'Listening… Stop when you are ready to review.'}</p>
        {voice.preview && <p dir="auto" className="mt-1 max-h-20 overflow-y-auto whitespace-pre-wrap break-words text-gray-700">{voice.preview}</p>}
      </div>}
      {voice.error && <p role="alert" className="mt-2 px-1 text-xs text-amber-700">{voice.error}</p>}
    </>
  );
}
